import { useState, useCallback } from "react";

export type Iperf3State =
  | "idle"
  | "starting-server"
  | "running-test"
  | "completed"
  | "error";

export interface Iperf3Results {
  sentMbps: number;
  receivedMbps: number;
  retransmits: number;
  durationSeconds: number;
}

export function useIperf3Test() {
  const [state, setState] = useState<Iperf3State>("idle");
  const [results, setResults] = useState<Iperf3Results | null>(null);
  const [error, setError] = useState<string | null>(null);

  const startTest = useCallback(async (bridgeUrl: string) => {
    setState("starting-server");
    setResults(null);
    setError(null);

    try {
      // Step 1: Ask the API server to spawn an iperf3 server
      const startRes = await fetch("/api/iperf3/start", { method: "POST" });
      if (!startRes.ok) {
        const body = await startRes.json().catch(() => ({}));
        throw new Error(body.error || `API server returned ${startRes.status}`);
      }
      const { port, host } = (await startRes.json()) as {
        port: number;
        host: string;
      };

      // Step 2: Ask the bridge to run iperf3 client against the API server
      setState("running-test");
      const runRes = await fetch(`${bridgeUrl}/api/bandwidth-test/run`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ host, port }),
      });

      if (!runRes.ok) {
        const body = await runRes.json().catch(() => ({}));
        throw new Error(body.error || `Bridge returned ${runRes.status}`);
      }

      const iperf3Output = await runRes.json();

      // Extract results from iperf3 JSON output
      const end = iperf3Output?.end;
      if (!end) {
        throw new Error("Unexpected iperf3 output format");
      }

      const senderStream = end.sum_sent || end.streams?.[0]?.sender;
      const receiverStream = end.sum_received || end.streams?.[0]?.receiver;

      const sentMbps = senderStream
        ? senderStream.bits_per_second / 1_000_000
        : 0;
      const receivedMbps = receiverStream
        ? receiverStream.bits_per_second / 1_000_000
        : 0;
      const retransmits = senderStream?.retransmits ?? 0;
      const durationSeconds = senderStream?.seconds ?? 20;

      setResults({ sentMbps, receivedMbps, retransmits, durationSeconds });
      setState("completed");
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setError(message);
      setState("error");
    }
  }, []);

  const reset = useCallback(() => {
    setState("idle");
    setResults(null);
    setError(null);
  }, []);

  return { state, results, error, startTest, reset };
}
